import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { faqItems } from '../data/seo';

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [searchTerm, setSearchTerm] = useState('');

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const filteredFaqs = faqItems.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <section id="faq" className="bg-white section-padding">
      <div className="container-custom">
        <div className="text-center mb-12 lg:mb-16 px-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-hellotend-brand-brown mb-6 lg:mb-8 leading-[1.1] font-nantes" style={{ fontWeight: 700 }}>
            Frequently Asked Questions
          </h2>
          <p className="text-base sm:text-lg text-hellotend-neutral-700 max-w-3xl mx-auto mb-6 lg:mb-8 font-founders" style={{ fontWeight: 400 }}>
            Get answers to the most frequently asked questions about pet dental care
          </p>

          <div className="max-w-md mx-auto">
            <label htmlFor="faq-search" className="sr-only">
              Search frequently asked questions
            </label>
            <div className="relative">
              <input
                id="faq-search"
                type="text"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 sm:p-4 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-transparent text-gray-700 text-sm sm:text-base"
              />
              <i className="fas fa-search absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-gray-400" aria-hidden="true"></i>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => {
                const isOpen = openIndex === index;
                return (
                  <div
                    key={faq.question}
                    className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-fit"
                  >
                    <button
                      type="button"
                      className="w-full text-left p-5 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-opacity-50 rounded-xl flex items-start justify-between gap-4"
                      onClick={() => toggleFAQ(index)}
                      aria-expanded={isOpen}
                    >
                      <h3 className="text-base sm:text-lg font-semibold text-primary leading-snug">
                        {faq.question}
                      </h3>
                      <div
                        className={`flex-shrink-0 w-6 h-6 bg-brand-teal rounded-full flex items-center justify-center text-white transition-transform duration-300 mt-0.5 ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      >
                        <i className="fas fa-chevron-down text-[10px]"></i>
                      </div>
                    </button>

                    {/* Keep answers in the DOM for crawlers even when visually collapsed */}
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        isOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                      }`}
                      aria-hidden={!isOpen}
                    >
                      <div className="px-5 pb-5">
                        <div className="pt-3 border-t border-gray-100">
                          <p className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-line">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-1 lg:col-span-2 text-center py-12">
                <i className="fas fa-search text-4xl text-gray-400 mb-4"></i>
                <p className="text-xl text-gray-600">No FAQs found matching your search.</p>
                <p className="text-gray-500">Try different keywords or browse all questions above.</p>
              </div>
            )}
          </div>
        </div>

        <div className="text-center mt-12">
          <div className="bg-gradient-to-br from-card-bg to-orange-50 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-primary mb-4">Still have questions?</h3>
            <p className="text-gray-600 mb-6">
              Our caring team is here to help. Give us a call or send us a message.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:info@chomp.vet" className="btn-primary inline-flex items-center justify-center">
                <i className="fas fa-envelope mr-2"></i>
                Email info@chomp.vet
              </a>
              <Link to="/contact" className="btn-secondary inline-flex items-center justify-center">
                <i className="fas fa-comment mr-2"></i>
                Send Message
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
